import { useEffect, useRef, useState } from 'react';
import { trackAiSearchChipRemoval } from '@pkgs/components/utils/tracking';
import { type FilterChip, removeAiSearchFilterChip } from '@pkgs/components/utils/aiSearchChipUtils';
import { useSearchModuleTracking } from '@pkgs/components/hooks/useSearchModuleTracking';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useAutoCompleteStore } from '@pkgs/components/hooks/stores/useAutoCompleteStore';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { usePromptStore } from '@pkgs/components/hooks/stores/search/usePromptStore';
import { usePlaceDetails } from '@pkgs/components/hooks/queries/usePlaceDetails';

import { MobileAiSearchButton } from '../MobileAiSearchButton/MobileAiSearchButton';
import { AiPromptInput } from '../AiPromptInput/AiPromptInput';

import styles from './AiLocationFilter.module.css';

export const MOBILE_AI_PROMPT_FORM_ID = 'ai-prompt-form-mobile';

export const MobileAiPromptInput = () => {
  const { countryCode } = useAppContext();
  const promptValue = usePromptStore((state) => state.promptValue);
  const { placeDetailsList } = usePlaceDetails();
  const [temporaryPromptValue, setTemporaryPromptValue] = useState(promptValue);
  const ref = useRef<HTMLDivElement>(null);
  const getStateSnapshot = useSearchStore((state) => state.getStateSnapshot);
  const applyState = useSearchStore((state) => state.applyState);
  const hasPendingFilterChanges = useAutoCompleteStore((state) => Boolean(state.aiDraftState));
  const setAiDraftState = useAutoCompleteStore((state) => state.setAiDraftState);
  const resetAiDraft = useAutoCompleteStore((state) => state.resetAiDraft);
  const setIsMobileModalOpen = useAutoCompleteStore((state) => state.setIsMobileModalOpen);
  const { pageContext, shopContext } = useSearchModuleTracking();

  useEffect(() => {
    setTemporaryPromptValue(promptValue);
  }, [promptValue]);

  const handleChipDelete = (chip: FilterChip) => {
    trackAiSearchChipRemoval({
      source: 'drawer',
      chipVariant: chip.variant,
      pageContext,
      shopContext,
    });

    const aiDraftState = useAutoCompleteStore.getState().aiDraftState;
    const nextState = removeAiSearchFilterChip({
      state: aiDraftState ?? getStateSnapshot(),
      chip,
      marketCountryCode: countryCode,
      placeDetails: placeDetailsList,
    });
    setAiDraftState?.(nextState);
  };

  const handleApplyPendingFilters = () => {
    const aiDraftState = useAutoCompleteStore.getState().aiDraftState;
    if (!aiDraftState) return;
    applyState({ state: aiDraftState });
    resetAiDraft?.();
  };

  return (
    <AiPromptInput
      className={styles.mobileContainer}
      contentClassName={styles.mobileContentWrapper}
      externalRef={ref}
      isDropdownOpen
      formId={MOBILE_AI_PROMPT_FORM_ID}
      onChangeHandler={setTemporaryPromptValue}
      temporaryPromptValue={temporaryPromptValue}
      shouldShowClearButton={Boolean(temporaryPromptValue)}
      hasPendingFilterChanges={hasPendingFilterChanges}
      shouldUseDraftFilters
      onChipDelete={handleChipDelete}
      onApplyPendingFilters={handleApplyPendingFilters}
      onSubmit={() => setIsMobileModalOpen(false)}
    >
      <MobileAiSearchButton temporaryPromptValue={temporaryPromptValue} />
    </AiPromptInput>
  );
};
